import React, { useState } from "react";

import { Text, EnhancedTable, Menu } from "@pushpress/shared-components";
import { useHistory, useRouteMatch } from "react-router-dom";

import type { CalendarItemsListFilterValue } from "./CalendarItemsListFilter";
import type {
  CalendarItemsListRowFragment,
  ListCalendarItemsQuery,
} from "./CalendarItemsList-generated.hooks";
import type { DataTableSort } from "@pushpress/shared-components/build/components/data/DataTable";
import type { DateRangeValue } from "@pushpress/shared-components/build/components/selectors/DateRangeSelector/DateRangeSelector.types";

import CalendarItemsListFilter from "./CalendarItemsListFilter";
import DateTimeStartEndCell from "../../../components/table/DateTimeStartEndCell";
import StaffCard from "../../../components/cards/StaffCard";
import CancelCalendarItemModal from "../CalendarForm/CancelCalendarItem";
import { CalendarItemSessionTypes } from "../Calendar.utils";
import { isCypress } from "../../../utils/cypress";

const getRowId = (row: CalendarItemsListRowFragment) => {
  return row.uuid;
};

interface CalendarItemsListTableProps {
  calendarItemType: CalendarItemSessionTypes;
  data?: ListCalendarItemsQuery;
  loading: boolean;
  viewSaved: boolean;
  dateRangeValue: DateRangeValue;
  filter: CalendarItemsListFilterValue;
  filterTagOptions: {
    label: string;
    value: string;
  }[];
  page: number;
  sort: DataTableSort<string>;
  handleFilterChange: (filter?: CalendarItemsListFilterValue) => void;
  handleFilterTagDelete: (key: string) => void;
  handleViewSave: VoidFunction;
  setDateRangeValue: (dateRangeValue: DateRangeValue) => void;
  setPage: (page: number) => void;
  setSort: (sort: DataTableSort<string>) => void;
  refetch: VoidFunction;
}

function CalendarItemsListTable({
  calendarItemType,
  data,
  loading,
  viewSaved,
  dateRangeValue,
  filter,
  filterTagOptions,
  page,
  sort,
  handleFilterChange,
  handleFilterTagDelete,
  handleViewSave,
  setDateRangeValue,
  setPage,
  setSort,
  refetch,
}: CalendarItemsListTableProps) {
  const routeMatch = useRouteMatch();
  const path = isCypress ? `/schedule/list` : routeMatch?.path;
  const [cancelingCalendarItem, setCancelingCalendarItem] =
    useState<CalendarItemsListRowFragment>();
  const history = useHistory();

  const onSort = (newSort: DataTableSort<string>) => {
    if (sort?.key != newSort?.key) {
      // any time the sort column changes, start ascending
      setSort({ key: newSort.key, order: "asc" });
    } else {
      setSort(newSort);
    }
    setPage(1);
  };

  const onPageChange = (page: number) => {
    setPage(Math.max(page, 1));
  };

  const onDateRangeChange = (newDateRange: DateRangeValue) => {
    setDateRangeValue(newDateRange);
    setPage(1);
  };

  const onFilterChange = (filter: CalendarItemsListFilterValue) => {
    handleFilterChange(filter);
    setPage(1);
  };

  const onFilterTagDelete = (value: string) => {
    handleFilterTagDelete(value);
    setPage(1);
  };

  const renderFilter = (
    filterForRender: CalendarItemsListFilterValue,
    handleFilterChangeForRender: (filter: CalendarItemsListFilterValue) => void,
    handleClearForRender: VoidFunction,
  ) => {
    return (
      <CalendarItemsListFilter
        filter={filterForRender}
        calendarItemType={calendarItemType}
        onFilterChange={handleFilterChangeForRender}
        onClear={handleClearForRender}
      />
    );
  };

  const isClassOrEvent =
    calendarItemType === CalendarItemSessionTypes.Classes ||
    calendarItemType === CalendarItemSessionTypes.Events;

  const renderRowMenu = (
    row: CalendarItemsListRowFragment,
    closeMenu: () => void,
  ) => {
    return (
      <Menu>
        <Menu.Item
          label="View"
          action={() => {
            history.push(`/schedule/${row.uuid}`);
            closeMenu();
          }}
        />
        {calendarItemType === CalendarItemSessionTypes.Events && (
          <Menu.Item
            type="link"
            label="Landing page"
            href={`/landing/events/${row?.uuid}`}
            target="_blank"
          />
        )}
        <Menu.Item
          label="Edit"
          action={() => {
            closeMenu();
            if (calendarItemType === CalendarItemSessionTypes.Events) {
              history.push(`${path}/edit/event/${row?.uuid}`);
            } else {
              history.push(`${path}/edit/class/${row?.uuid}`);
            }
          }}
        />
        {isClassOrEvent && (
          <Menu.Item
            label="Duplicate"
            action={() => {
              closeMenu();
              history.push(
                `${path}/duplicate/${
                  calendarItemType === CalendarItemSessionTypes.Classes
                    ? "class"
                    : "event"
                }/${row?.uuid}`,
              );
            }}
          />
        )}
        <Menu.Item
          label="Delete"
          action={() => {
            setCancelingCalendarItem(row);
            closeMenu();
          }}
        />
      </Menu>
    );
  };

  const columns = [
    {
      key: "calendarItemTypeName",
      header: "Name",
      isSortable: true,
      hideOnMobile: false,
      width: "25%",
      cell: (row: CalendarItemsListRowFragment) => {
        return (
          <>
            <Text variant="body-lg" color="wolf-800">
              {row.title}
            </Text>
            {calendarItemType === CalendarItemSessionTypes.Classes && (
              <Text variant="body-lg" color="wolf-600">
                {row.calendarItemType?.name}
              </Text>
            )}
          </>
        );
      },
    },
    {
      key: "startDatetime",
      header: "Date",
      isSortable: true,
      cell: (row: CalendarItemsListRowFragment) => (
        // Always display CalendarItem times in UTC
        //   (see https://www.notion.so/pushpress/DateTimes-and-Timezones-in-the-Graph-297222589b324242ab59564d635790aa?pvs=4)
        <DateTimeStartEndCell
          startTime={row.startDatetime}
          endTime={row.endDatetime}
          timezone={"UTC"}
        />
      ),
    },
    {
      key: "mainCoachFirstName",
      header: "Staff",
      isSortable: true,
      hideOnMobile: true,
      width: "25%",
      cell: (row: CalendarItemsListRowFragment) => {
        if (!row.mainCoach?.firstName && !row.mainCoach?.lastName) {
          return "- -";
        }
        return (
          <StaffCard
            staff={{
              firstName: row.mainCoach.firstName || undefined,
              lastName: row.mainCoach.lastName || undefined,
            }}
          />
        );
      },
    },
    {
      key: "attendanceCount",
      header: "Attendance",
      isSortable: true,
      hideOnMobile: true,
      width: "25%",
      cell: (row: CalendarItemsListRowFragment) => {
        return (
          <>
            <Text variant="body-lg" color="wolf-800">
              {row.attendanceCount || 0} /{" "}
              {row.attendanceCap > 0 ? row.attendanceCap : <>&infin;</>}
            </Text>
          </>
        );
      },
    },
  ];

  return (
    <>
      <CancelCalendarItemModal
        uuid={cancelingCalendarItem?.uuid}
        visible={!!cancelingCalendarItem}
        onClose={() => setCancelingCalendarItem(undefined)}
        onCalendarItemCancelled={refetch}
      />
      <EnhancedTable
        columns={columns}
        data={data?.listCalendarItems.items || []}
        dateRange={dateRangeValue}
        filter={filter}
        filterTagOptions={filterTagOptions}
        loading={loading}
        sort={sort}
        page={page}
        morePages={data?.listCalendarItems.meta?.hasMore === true}
        viewSaved={viewSaved}
        getRowId={getRowId}
        onDateRangeChange={onDateRangeChange}
        onFilterChange={onFilterChange}
        onFilterTagDelete={onFilterTagDelete}
        onPageChange={onPageChange}
        onSort={onSort}
        onViewSave={handleViewSave}
        renderFilter={renderFilter}
        renderRowMenu={renderRowMenu}
      />
    </>
  );
}

export default CalendarItemsListTable;
